/* ============================================================
   video-scroll-story.js
   - First slide pins (sticky inner stage). While pinned, the
     heading is stationary and the body/button are scrubbed up
     from the bottom of the screen to their docked position.
   - The first slide's body is split into individual sentences
     (on "."), and each sentence + the button rise in ONE AT A
     TIME, in a smooth, eased, staggered cascade.
   - After the pin's extra scroll is consumed, the slide releases
     and scrolls away; the next slide scrolls in normally.
   - Slides 2+ are always visible (no reveal).
   - Video loops, or scrubs to whole-section scroll progress.
   - "Match content width" measures each heading's rendered width
     into --vss-head-w so the content below matches it.
   - Letter cascade: splits the first heading into per-letter spans
     that fade in left-to-right (opacity only), when the section
     first comes into view.
   - Publishes the OS scrollbar width into --vss-sbw so the CSS
     card-fit maths (which uses 100vw) matches the real usable
     width. Without this, a Windows scrollbar makes the fitted row
     ~15px too wide, which reintroduces a small pan and crops the
     first card.
   ============================================================ */
(function () {
  'use strict';

  var MOBILE_MQ = '(max-width: 749px)';
  function clamp01(n) { return n < 0 ? 0 : (n > 1 ? 1 : n); }
  function clamp(n, lo, hi) { return n < lo ? lo : (n > hi ? hi : n); }
  function easeOutCubic(t) { return 1 - Math.pow(1 - t, 3); }

  /* Scrollbar width -> --vss-sbw on the section root. */
  function syncScrollbarWidth(root) {
    var sbw = window.innerWidth - document.documentElement.clientWidth;
    if (!sbw || sbw < 0 || isNaN(sbw)) sbw = 0;
    root.style.setProperty('--vss-sbw', sbw + 'px');
  }

  function initStory(root) {
    if (!root || root.dataset.vssInit === '1') return;
    root.dataset.vssInit = '1';

    /* Set before anything else so the very first paint of the card row
       already uses the correct fit width. */
    syncScrollbarWidth(root);
    window.addEventListener('resize', function () { syncScrollbarWidth(root); });

    var mode = root.getAttribute('data-playback') || 'loop';
    var contentScroll = root.getAttribute('data-content-scroll') === 'true';
    var headingAnim = root.getAttribute('data-heading-anim') === 'true';
    var span = parseFloat(root.getAttribute('data-span')) || 0.85;
    if (span <= 0) span = 1;
    var matchWidth = root.classList.contains('vss--match-width');

    var mq = window.matchMedia(MOBILE_MQ);
    var reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    var desktopVid = root.querySelector('.vss-video--desktop');
    var mobileVid = root.querySelector('.vss-video--mobile');

    var firstScene = root.querySelector('.vss-scene--first');
    var firstPin = firstScene ? firstScene.querySelector('.vss-pin') : null;
    var firstBelow = firstScene ? firstScene.querySelector('.vss-below') : null;
    var animStyle = firstScene ? (firstScene.getAttribute('data-anim') || '') : '';
    var headingRevealed = false;

    /* Per-line rise state (first slide only) */
    var firstLines = [];    // one element per sentence + the button
    var lineMetrics = [];   // cached park offset (px) for each line

    function activeVideo() { return mq.matches ? (mobileVid || desktopVid) : (desktopVid || mobileVid); }
    function inactiveVideo() { return mq.matches ? desktopVid : mobileVid; }
    function pause(v) { if (v) { try { v.pause(); } catch (e) {} } }
    function playLoop(v) { if (!v) return; v.loop = true; var p = v.play(); if (p && p.catch) p.catch(function () {}); }

    /* Split the first heading into per-letter spans for the cascade. */
    function splitHeading(scene) {
      var hEl = scene.querySelector('.vss-heading');
      if (!hEl || hEl.dataset.vssSplit === '1') return;

      var text = hEl.textContent;
      hEl.setAttribute('aria-label', text);
      hEl.dataset.vssSplit = '1';

      var words = text.match(/\S+|\s+/g) || [text];

      var visibleCount = 0;
      for (var w = 0; w < words.length; w++) {
        var chars = words[w].split('');
        for (var c = 0; c < chars.length; c++) {
          if (chars[c].trim() !== '') visibleCount++;
        }
      }

      var perLetterDur = 650;
      var lastStart = 900;
      var step = visibleCount > 1 ? clamp(lastStart / (visibleCount - 1), 28, 80) : 0;

      var frag = document.createDocumentFragment();
      var vi = 0;

      for (var w = 0; w < words.length; w++) {
        var word = words[w];

        if (word.trim() === '') {
          frag.appendChild(document.createTextNode(word));
          continue;
        }

        var wordSpan = document.createElement('span');
        wordSpan.className = 'vss-word';
        wordSpan.style.display = 'inline-block';
        wordSpan.style.whiteSpace = 'nowrap';

        var chars = word.split('');
        for (var c = 0; c < chars.length; c++) {
          var ch = chars[c];
          if (ch.trim() === '') {
            wordSpan.appendChild(document.createTextNode(ch));
            continue;
          }
          var charSpan = document.createElement('span');
          charSpan.className = 'vss-char';
          charSpan.setAttribute('aria-hidden', 'true');
          charSpan.textContent = ch;
          charSpan.style.transitionDuration = perLetterDur + 'ms';
          charSpan.style.transitionDelay = (vi * step) + 'ms';
          vi++;
          wordSpan.appendChild(charSpan);
        }

        frag.appendChild(wordSpan);
      }

      hEl.textContent = '';
      hEl.appendChild(frag);
      hEl.classList.add('vss-split');
    }

    /* Split the first slide's body into sentence lines. */
    function splitBodyLines(belowEl) {
      if (!belowEl) return [];
      if (belowEl.dataset.vssLines === '1') {
        return Array.prototype.slice.call(belowEl.querySelectorAll('.vss-line'));
      }

      var lines = [];
      var bodyEl = belowEl.querySelector('.vss-body');
      var sentenceRe = /[^.]+\.?/g;

      if (bodyEl) {
        var data = [];

        function pushSentences(txt) {
          var parts = txt.match(sentenceRe) || [];
          for (var i = 0; i < parts.length; i++) {
            var s = parts[i].trim();
            if (s) data.push({ text: s });
          }
        }

        var blocks = bodyEl.children.length
          ? Array.prototype.slice.call(bodyEl.children)
          : null;

        if (blocks) {
          for (var b = 0; b < blocks.length; b++) {
            var blk = blocks[b];
            var txt = (blk.textContent || '').trim();
            if (!txt) continue;

            var count = 0;
            var matches = txt.match(sentenceRe) || [];
            for (var j = 0; j < matches.length; j++) { if (matches[j].trim()) count++; }

            if (count <= 1) {
              data.push({ html: blk.innerHTML.trim() });
            } else {
              pushSentences(txt);
            }
          }
        } else {
          pushSentences((bodyEl.textContent || '').trim());
        }

        bodyEl.innerHTML = '';
        for (var d = 0; d < data.length; d++) {
          var line = document.createElement('div');
          line.className = 'vss-line';
          if (data[d].html != null) line.innerHTML = data[d].html;
          else line.textContent = data[d].text;
          bodyEl.appendChild(line);
          lines.push(line);
        }
      }

      var btn = belowEl.querySelector('.vss-button');
      if (btn) {
        var wrap = document.createElement('div');
        wrap.className = 'vss-line vss-line--button';
        btn.parentNode.insertBefore(wrap, btn);
        wrap.appendChild(btn);
        lines.push(wrap);
      }

      belowEl.dataset.vssLines = '1';
      return lines;
    }

    /* Measure each line's DOCKED position once (transform cleared). */
    function measureLines(vh) {
      if (!firstLines.length || !firstBelow) return;
      var pinEl = firstPin || firstScene;
      var prev = [];
      var i;
      for (i = 0; i < firstLines.length; i++) {
        prev[i] = firstLines[i].style.transform;
        firstLines[i].style.transform = 'none';
      }
      void firstBelow.offsetWidth;
      var base = pinEl ? pinEl.getBoundingClientRect().top : 0;
      var metrics = [];
      for (i = 0; i < firstLines.length; i++) {
        var r = firstLines[i].getBoundingClientRect();
        var dockedY = r.top - base;
        metrics[i] = Math.max((vh - dockedY) + r.height + 2, 0);
      }
      for (i = 0; i < firstLines.length; i++) {
        firstLines[i].style.transform = prev[i];
      }
      lineMetrics = metrics;
    }

    function scheduleLineMeasure() {
      if (!firstLines.length) return;
      var run = function () { measureLines(window.innerHeight); };
      window.requestAnimationFrame(run);
      if (document.fonts && document.fonts.ready) {
        document.fonts.ready.then(run).catch(function () {});
      }
      setTimeout(run, 400);
    }

    function revealHeading() {
      if (headingRevealed || !firstScene) return;
      headingRevealed = true;
      void firstScene.offsetWidth;
      window.requestAnimationFrame(function () {
        window.requestAnimationFrame(function () {
          firstScene.classList.add('is-title-in');
        });
      });
    }

    function measureMatchWidth() {
      if (!matchWidth) return;
      var headings = root.querySelectorAll('.vss-heading');
      for (var i = 0; i < headings.length; i++) {
        var hEl = headings[i];
        var content = hEl.closest ? hEl.closest('.vss-content') : null;
        if (!content) continue;
        var w = hEl.getBoundingClientRect().width;
        if (w > 0) content.style.setProperty('--vss-head-w', Math.ceil(w) + 'px');
        else content.style.removeProperty('--vss-head-w');
      }
    }
    function scheduleMatchWidth() {
      if (!matchWidth) return;
      window.requestAnimationFrame(measureMatchWidth);
      if (document.fonts && document.fonts.ready) {
        document.fonts.ready.then(measureMatchWidth).catch(function () {});
      }
      var dur = parseFloat(getComputedStyle(root).getPropertyValue('--vss-anim-dur')) || 800;
      setTimeout(measureMatchWidth, dur + 150);
    }

    /* ---- Reduced motion: show everything, no rise, no scrub ---- */
    if (reduce) {
      root.classList.add('vss-no-anim');
      if (firstScene) firstScene.classList.add('is-title-in');
      if (firstBelow) firstBelow.style.transform = 'none';
      scheduleMatchWidth();
      if (mode === 'loop') { pause(inactiveVideo()); playLoop(activeVideo()); }
      return;
    }

    root.classList.add('vss-ready');

    root.style.setProperty('--vss-span', String(contentScroll ? span : 1));

    if (firstScene && headingAnim && animStyle === 'letters') {
      splitHeading(firstScene);
    }

    if (firstScene && contentScroll && firstBelow) {
      firstLines = splitBodyLines(firstBelow);
      if (firstLines.length) {
        firstBelow.style.transform = 'none';
        scheduleLineMeasure();
      }
    }

    scheduleMatchWidth();

    var rafId = null;
    var inView = true;
    var smoothTime = 0;
    var smoothRise = 0;
    var riseInit = false;
    var lastT = 0;

    function pinInView() {
      var el = firstPin || firstScene || root;
      var r = el.getBoundingClientRect();
      var vh = window.innerHeight || document.documentElement.clientHeight;
      var visible = Math.min(r.bottom, vh) - Math.max(r.top, 0);
      return visible > Math.min(r.height, vh) * 0.4;
    }

    function maybeRevealHeading() {
      if (!headingRevealed && pinInView()) revealHeading();
    }

    function sectionScrolledPx(vh) {
      var r = root.getBoundingClientRect();
      var max = Math.max(root.offsetHeight - vh, 0);
      var s = -r.top;
      return s < 0 ? 0 : (s > max ? max : s);
    }

    function updateFirstRise(vh, dt) {
      if (!firstScene || !contentScroll || !firstLines.length) return;

      var rect = firstScene.getBoundingClientRect();
      var target;
      if (rect.top > 0.5) {
        target = 0;
      } else {
        var pinPx = Math.max(firstScene.offsetHeight - vh, 1);
        target = clamp01(-rect.top / pinPx);
      }

      var SMOOTH_TAU = 120;
      if (!riseInit || Math.abs(target - smoothRise) > 0.4) {
        smoothRise = target;
        riseInit = true;
      } else {
        var alpha = 1 - Math.exp(-dt / SMOOTH_TAU);
        smoothRise += (target - smoothRise) * alpha;
        if (Math.abs(target - smoothRise) < 0.0004) smoothRise = target;
      }

      var riseP = smoothRise;
      var n = firstLines.length;
      var lineDur = n > 1 ? 0.55 : 1;
      var stagger = n > 1 ? (1 - lineDur) / (n - 1) : 0;
      var ready = lineMetrics.length === n;

      for (var i = 0; i < n; i++) {
        var lp = clamp01((riseP - i * stagger) / lineDur);
        var park = ready ? lineMetrics[i] : vh * 1.2;
        var ty = park * (1 - easeOutCubic(lp));
        firstLines[i].style.transform = ty > 0.1
          ? 'translate3d(0,' + ty + 'px,0)'
          : 'translate3d(0,0,0)';
      }
    }

    function frame(now) {
      now = now || (window.performance ? performance.now() : Date.now());
      if (!lastT) lastT = now;
      var dt = now - lastT;
      if (dt > 50) dt = 50;
      lastT = now;

      if (inView) {
        var vh = window.innerHeight;
        maybeRevealHeading();
        updateFirstRise(vh, dt);

        if (mode === 'scroll') {
          var vid = activeVideo();
          if (vid && vid.duration && !isNaN(vid.duration)) {
            var max = Math.max(root.offsetHeight - vh, 0);
            var prog = max > 0 ? sectionScrolledPx(vh) / max : 0;
            var target = prog * vid.duration;
            smoothTime += (target - smoothTime) * 0.12;
            if (Math.abs(target - smoothTime) < 0.015) smoothTime = target;
            try { vid.currentTime = smoothTime; } catch (e) {}
          }
        }
      }
      rafId = window.requestAnimationFrame(frame);
    }

    function startScroll() {
      pause(desktopVid); pause(mobileVid);
      if (desktopVid) desktopVid.loop = false;
      if (mobileVid) mobileVid.loop = false;
      var vid = activeVideo();
      if (vid) {
        vid.muted = true;
        var p = vid.play();
        if (p && p.then) { p.then(function () { vid.pause(); }).catch(function () {}); }
      }
    }

    function applyMode() {
      if (mode === 'scroll') { startScroll(); }
      else { pause(inactiveVideo()); playLoop(activeVideo()); }
    }

    if ('IntersectionObserver' in window) {
      var io = new IntersectionObserver(function (entries) {
        entries.forEach(function (entry) {
          inView = entry.isIntersecting;
          if (inView) {
            maybeRevealHeading();
            measureLines(window.innerHeight);
          }
          if (mode === 'loop') {
            if (inView) playLoop(activeVideo());
            else pause(activeVideo());
          }
        });
      }, { threshold: 0 });
      io.observe(root);
    }

    window.requestAnimationFrame(maybeRevealHeading);
    setTimeout(maybeRevealHeading, 250);

    var rt = null;
    window.addEventListener('resize', function () {
      clearTimeout(rt);
      rt = setTimeout(function () {
        syncScrollbarWidth(root);
        measureLines(window.innerHeight);
        measureMatchWidth();
        maybeRevealHeading();
      }, 150);
    });

    function onBreakpointChange() {
      smoothTime = 0;
      pause(desktopVid);
      pause(mobileVid);
      syncScrollbarWidth(root);
      measureLines(window.innerHeight);
      measureMatchWidth();
      applyMode();
    }
    if (mq.addEventListener) mq.addEventListener('change', onBreakpointChange);
    else if (mq.addListener) mq.addListener(onBreakpointChange);

    applyMode();
    rafId = window.requestAnimationFrame(frame);
  }

  function boot() {
    document.querySelectorAll('.vss').forEach(initStory);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

  /* Theme editor support */
  document.addEventListener('shopify:section:load', function (e) {
    var el = e.target.querySelector('.vss');
    if (el) {
      el.dataset.vssInit = '';
      initStory(el);
    }
  });
})();

/* ============================================================
   Card blocks — separate, self-contained controller.

   Horizontal, scroll-driven:
     The row pans by exactly the measured overflow. With the CSS
     card-fit in place the overflow is normally 0 on desktop, so
     there is no pan and no card can be clipped by the pinned
     stage. A small residual overflow (< PAN_MIN px, e.g. from
     sub-pixel rounding) is also ignored, so the first card can
     never be nudged half a pixel off the left edge.
   ============================================================ */
(function () {
  'use strict';

  function clamp01(n) { return n < 0 ? 0 : (n > 1 ? 1 : n); }
  function clamp(n, lo, hi) { return n < lo ? lo : (n > hi ? hi : n); }
  function easeOutCubic(t) { return 1 - Math.pow(1 - t, 3); }

  var EDGE_GAP = 24; // px past the stage edge a parked card sits
  var PAN_MIN = 8;   // ignore overflow smaller than this — not worth panning for

  var scrubStages = [];
  var rafId = null;
  var lastT = 0;

  function splitHeading(scope) {
    var hEl = scope.querySelector('.vss-heading');
    if (!hEl || hEl.dataset.vssSplit === '1') return;

    var text = hEl.textContent;
    hEl.setAttribute('aria-label', text);
    hEl.dataset.vssSplit = '1';

    var words = text.match(/\S+|\s+/g) || [text];

    var visibleCount = 0;
    for (var w = 0; w < words.length; w++) {
      var chars = words[w].split('');
      for (var c = 0; c < chars.length; c++) {
        if (chars[c].trim() !== '') visibleCount++;
      }
    }

    var perLetterDur = 650;
    var lastStart = 900;
    var step = visibleCount > 1 ? clamp(lastStart / (visibleCount - 1), 28, 80) : 0;

    var frag = document.createDocumentFragment();
    var vi = 0;

    for (var w2 = 0; w2 < words.length; w2++) {
      var word = words[w2];
      if (word.trim() === '') {
        frag.appendChild(document.createTextNode(word));
        continue;
      }
      var wordSpan = document.createElement('span');
      wordSpan.className = 'vss-word';
      wordSpan.style.display = 'inline-block';
      wordSpan.style.whiteSpace = 'nowrap';

      var wchars = word.split('');
      for (var c2 = 0; c2 < wchars.length; c2++) {
        var ch = wchars[c2];
        if (ch.trim() === '') {
          wordSpan.appendChild(document.createTextNode(ch));
          continue;
        }
        var charSpan = document.createElement('span');
        charSpan.className = 'vss-char';
        charSpan.setAttribute('aria-hidden', 'true');
        charSpan.textContent = ch;
        charSpan.style.transitionDuration = perLetterDur + 'ms';
        charSpan.style.transitionDelay = (vi * step) + 'ms';
        vi++;
        wordSpan.appendChild(charSpan);
      }
      frag.appendChild(wordSpan);
    }

    hEl.textContent = '';
    hEl.appendChild(frag);
    hEl.classList.add('vss-split');
  }

  function initHeading(root, stage) {
    var head = stage.querySelector('.vss-cards-head');
    if (!head) return;

    var headingAnim = root.getAttribute('data-heading-anim') === 'true';
    var reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    if (!headingAnim || reduce) {
      head.classList.add('is-title-in');
      return;
    }

    if ((head.getAttribute('data-anim') || '') === 'letters') splitHeading(head);

    var revealed = false;
    function reveal() {
      if (revealed) return;
      revealed = true;
      void head.offsetWidth;
      window.requestAnimationFrame(function () {
        window.requestAnimationFrame(function () {
          head.classList.add('is-title-in');
        });
      });
    }

    function inView() {
      var r = head.getBoundingClientRect();
      var vh = window.innerHeight || document.documentElement.clientHeight;
      return r.top < vh * 0.92 && r.bottom > 0;
    }

    if ('IntersectionObserver' in window) {
      var io = new IntersectionObserver(function (entries) {
        entries.forEach(function (e) {
          if (e.isIntersecting) { reveal(); io.disconnect(); }
        });
      }, { threshold: 0, rootMargin: '0px 0px -8% 0px' });
      io.observe(head);
    }
    window.requestAnimationFrame(function () { if (inView()) reveal(); });
    setTimeout(function () { if (inView()) reveal(); }, 250);
  }

  function cardsOf(stage) {
    return Array.prototype.slice.call(stage.querySelectorAll('.vss-card'));
  }

  function showAll(cards) {
    for (var i = 0; i < cards.length; i++) {
      cards[i].classList.add('is-card-in');
      cards[i].style.transitionDelay = '0ms';
      cards[i].style.opacity = '';
      cards[i].style.transform = '';
    }
  }

  /* ---- Viewport reveal (no pin) ---- */
  function initViewport(root, stage) {
    var cards = cardsOf(stage);
    if (!cards.length) return;
    if (!('IntersectionObserver' in window)) { showAll(cards); return; }

    var stagger = parseFloat(getComputedStyle(root).getPropertyValue('--vss-card-stagger'));
    if (!stagger || isNaN(stagger)) stagger = 120;

    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (!entry.isIntersecting) return;
        var el = entry.target;
        var i = parseInt(el.getAttribute('data-vss-card-index') || '0', 10);
        el.style.transitionDelay = (i * stagger) + 'ms';
        el.classList.add('is-card-in');
        io.unobserve(el);
      });
    }, { threshold: 0.15, rootMargin: '0px 0px -6% 0px' });

    for (var i = 0; i < cards.length; i++) {
      cards[i].setAttribute('data-vss-card-index', String(i));
      io.observe(cards[i]);
    }
  }

  /* ---- Scroll-driven reveal (pinned + scrubbed) ---- */

  function measureGeometry(entry) {
    if (!entry.horizontal || !entry.rail || !entry.track) return;

    var prev = entry.track.style.transform;
    entry.track.style.transform = 'none';

    var prevCards = [];
    for (var i = 0; i < entry.cards.length; i++) {
      prevCards[i] = entry.cards[i].style.transform;
      entry.cards[i].style.transform = 'none';
    }

    void entry.track.offsetWidth; // one forced layout, no paint

    var railRect = entry.rail.getBoundingClientRect();
    var pinRect = entry.pin ? entry.pin.getBoundingClientRect() : railRect;
    var trackRect = entry.track.getBoundingClientRect();

    // Measure from the TRACK, not the rail: with the fit-to-row centring
    // the track can be narrower than the rail and centred inside it, so the
    // rail's left edge is no longer where card 0 starts.
    entry.railLeft = trackRect.left;
    entry.stageRight = pinRect.right;

    entry.offsets = [];
    for (var j = 0; j < entry.cards.length; j++) {
      entry.offsets[j] = entry.cards[j].offsetLeft;
    }

    // Only pan when the row genuinely doesn't fit. Sub-pixel overflow is
    // ignored so the first card can never be shaved off the left edge.
    var raw = entry.track.scrollWidth - entry.rail.clientWidth;
    entry.overflow = raw > PAN_MIN ? raw : 0;

    entry.track.style.transform = prev;
    for (var k = 0; k < entry.cards.length; k++) {
      entry.cards[k].style.transform = prevCards[k];
    }
    entry.measured = true;
  }

  function parkAll(entry) {
    for (var i = 0; i < entry.cards.length; i++) {
      if (entry.horizontal) {
        var park = entry.measured
          ? Math.max(entry.stageRight - (entry.railLeft + entry.offsets[i]) + EDGE_GAP, 0)
          : window.innerWidth;
        entry.cards[i].style.opacity = '1';
        entry.cards[i].style.transform = 'translate3d(' + park + 'px,0,0)';
      } else {
        entry.cards[i].style.opacity = '0';
        entry.cards[i].style.transform = 'translate3d(0,' + entry.dist + 'px,0)';
      }
    }
  }

  function registerScrub(stage) {
    var cards = cardsOf(stage);
    if (!cards.length) return;

    var horizontal = stage.getAttribute('data-card-mode') === 'scroll';
    var dist = parseFloat(getComputedStyle(stage).getPropertyValue('--vss-card-rise-dist'));
    if (!dist || isNaN(dist)) dist = 60;

    var entry = {
      stage: stage,
      pin: stage.querySelector('.vss-cards-pin'),
      cards: cards,
      rail: stage.querySelector('.vss-cards-rail'),
      track: stage.querySelector('.vss-cards'),
      horizontal: horizontal,
      dist: dist,
      offsets: [],
      railLeft: 0,
      stageRight: 0,
      overflow: 0,
      measured: false,
      smooth: 0,
      init: false,
      inView: false
    };

    parkAll(entry);

    if (horizontal) {
      var run = function () { measureGeometry(entry); };
      window.requestAnimationFrame(run);
      if (document.fonts && document.fonts.ready) {
        document.fonts.ready.then(run).catch(function () {});
      }
      setTimeout(run, 400);
    }

    if ('IntersectionObserver' in window) {
      var io = new IntersectionObserver(function (entries) {
        entries.forEach(function (e) {
          entry.inView = e.isIntersecting;
          if (e.isIntersecting && entry.horizontal) measureGeometry(entry);
        });
      }, { threshold: 0 });
      io.observe(stage);
    } else {
      entry.inView = true;
    }

    scrubStages.push(entry);
    startLoop();
  }

  function updateScrub(entry, vh, dt) {
    var rect = entry.stage.getBoundingClientRect();
    var target;
    if (rect.top > 0.5) {
      target = 0;
    } else {
      var pinPx = Math.max(entry.stage.offsetHeight - vh, 1);
      target = clamp01(-rect.top / pinPx);
    }

    var SMOOTH_TAU = 120;
    if (!entry.init || Math.abs(target - entry.smooth) > 0.4) {
      entry.smooth = target;
      entry.init = true;
    } else {
      var alpha = 1 - Math.exp(-dt / SMOOTH_TAU);
      entry.smooth += (target - entry.smooth) * alpha;
      if (Math.abs(target - entry.smooth) < 0.0004) entry.smooth = target;
    }

    var p = entry.smooth;
    var n = entry.cards.length;
    var itemDur = n > 1 ? 0.5 : 1;
    var stagger = n > 1 ? (1 - itemDur) / (n - 1) : 0;

    if (entry.horizontal) {
      // overflow is 0 when the row fits, so tx stays 0 and nothing pans.
      var tx = entry.overflow > 0 ? entry.overflow * p : 0;
      if (entry.track) entry.track.style.transform = 'translate3d(' + (-tx) + 'px,0,0)';

      for (var i = 0; i < n; i++) {
        var ip = clamp01((p - i * stagger) / itemDur);
        var e = easeOutCubic(ip);
        var dockedLeft = entry.measured
          ? entry.railLeft - tx + entry.offsets[i]
          : entry.railLeft;
        var park = entry.measured
          ? Math.max(entry.stageRight - dockedLeft + EDGE_GAP, 0)
          : window.innerWidth;
        var off = park * (1 - e);
        entry.cards[i].style.opacity = '1';
        entry.cards[i].style.transform = off > 0.1
          ? 'translate3d(' + off + 'px,0,0)'
          : 'translate3d(0,0,0)';
      }
      return;
    }

    for (var j = 0; j < n; j++) {
      var jp = clamp01((p - j * stagger) / itemDur);
      var je = easeOutCubic(jp);
      var jy = entry.dist * (1 - je);
      entry.cards[j].style.opacity = String(je);
      entry.cards[j].style.transform = jy > 0.1
        ? 'translate3d(0,' + jy + 'px,0)'
        : 'translate3d(0,0,0)';
    }
  }

  function frame(now) {
    now = now || (window.performance ? performance.now() : Date.now());
    if (!lastT) lastT = now;
    var dt = now - lastT;
    if (dt > 50) dt = 50;
    lastT = now;

    var vh = window.innerHeight;
    for (var i = 0; i < scrubStages.length; i++) {
      if (scrubStages[i].inView) updateScrub(scrubStages[i], vh, dt);
    }
    rafId = window.requestAnimationFrame(frame);
  }

  function startLoop() {
    if (rafId === null) rafId = window.requestAnimationFrame(frame);
  }

  /* ---- Arrows (horizontal + viewport reveal only) ---- */
  function initArrows(stage) {
    var rail = stage.querySelector('.vss-cards-rail');
    var prev = stage.querySelector('.vss-cards-arrow--prev');
    var next = stage.querySelector('.vss-cards-arrow--next');
    if (!rail || (!prev && !next)) return;

    function stepPx() {
      var card = rail.querySelector('.vss-card');
      var track = rail.querySelector('.vss-cards');
      var cs = track ? getComputedStyle(track) : null;
      var gap = cs ? (parseFloat(cs.columnGap || cs.gap) || 0) : 0;
      return card ? card.getBoundingClientRect().width + gap : rail.clientWidth * 0.8;
    }

    function syncArrows() {
      var max = rail.scrollWidth - rail.clientWidth - 2;
      // Hide the arrows entirely when the row already fits — nothing to page.
      var fits = rail.scrollWidth - rail.clientWidth <= PAN_MIN;
      if (prev) prev.style.display = fits ? 'none' : '';
      if (next) next.style.display = fits ? 'none' : '';
      if (prev) prev.disabled = rail.scrollLeft <= 2;
      if (next) next.disabled = rail.scrollLeft >= max;
    }

    if (prev) {
      prev.addEventListener('click', function () {
        rail.scrollBy({ left: -stepPx(), behavior: 'smooth' });
      });
    }
    if (next) {
      next.addEventListener('click', function () {
        rail.scrollBy({ left: stepPx(), behavior: 'smooth' });
      });
    }

    rail.addEventListener('scroll', syncArrows, { passive: true });
    window.addEventListener('resize', syncArrows);
    syncArrows();
    window.requestAnimationFrame(syncArrows);
  }

  function initCards(root) {
    if (!root || root.dataset.vssCardsInit === '1') return;
    var stages = root.querySelectorAll('.vss-scene--cards');
    if (!stages.length) return;
    root.dataset.vssCardsInit = '1';

    var reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    root.classList.add('vss-cards-ready');

    Array.prototype.forEach.call(stages, function (stage) {
      var reveal = stage.getAttribute('data-card-reveal');
      var mode = stage.getAttribute('data-card-mode');

      initHeading(root, stage);

      if (reduce) {
        showAll(cardsOf(stage));
      } else if (reveal === 'scroll') {
        registerScrub(stage);
      } else {
        initViewport(root, stage);
      }

      if (mode === 'scroll' && reveal === 'viewport') initArrows(stage);
    });
  }

  var crt = null;
  window.addEventListener('resize', function () {
    clearTimeout(crt);
    crt = setTimeout(function () {
      for (var i = 0; i < scrubStages.length; i++) {
        if (scrubStages[i].horizontal) measureGeometry(scrubStages[i]);
        scrubStages[i].init = false;
      }
    }, 150);
  });

  function bootCards() {
    document.querySelectorAll('.vss').forEach(initCards);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', bootCards);
  } else {
    bootCards();
  }

  document.addEventListener('shopify:section:load', function (e) {
    var el = e.target.querySelector('.vss');
    if (el) {
      scrubStages = scrubStages.filter(function (s) { return document.contains(s.stage); });
      el.dataset.vssCardsInit = '';
      initCards(el);
    }
  });

  document.addEventListener('shopify:block:select', function (e) {
    var t = e.target;
    if (!t || !t.classList) return;
    if (t.classList.contains('vss-cards-head') || t.querySelector('.vss-cards-head')) {
      var head = t.classList.contains('vss-cards-head') ? t : t.querySelector('.vss-cards-head');
      head.classList.add('is-title-in');
    }
    var card = t.classList.contains('vss-card') ? t : t.querySelector('.vss-card');
    if (card) {
      card.style.transitionDelay = '0ms';
      card.style.opacity = '1';
      card.style.transform = 'translate3d(0,0,0)';
      card.classList.add('is-card-in');
    }
  });
})();